import { useEffect, useState, useMemo, useCallback } from "react";
import { getPhotoUrl } from '@/utils/photoUrl';
import axios from "axios";
import { API } from "@/App";
import { useLanguage } from "@/i18n";
import { PageLayout } from "@/components/layout/Sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Trash2, Lock, Unlock, CheckSquare, Square, X, Eye, Search, Pencil } from "lucide-react";
import { EvaluationRow } from "@/components/evaluation/EvaluationRow";
import EditEvaluationDialog from "@/components/EditEvaluationDialog";

export default function EvaluationsPanel({ evaluator, onLogout }) {
  const [evaluations, setEvaluations] = useState([]);
  const { t } = useLanguage();
  const [players, setPlayers] = useState([]);
  const [evaluators, setEvaluators] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState("all");
  const [selectedEvaluator, setSelectedEvaluator] = useState("all");
  const [selectedPeriod, setSelectedPeriod] = useState("30d");
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [selectedEvaluation, setSelectedEvaluation] = useState(null);
  const [sortBy, setSortBy] = useState("date"); // 'date' | 'average'
  const [sortDirection, setSortDirection] = useState("desc"); // 'asc' | 'desc'
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [evaluationToDelete, setEvaluationToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [togglingBlockId, setTogglingBlockId] = useState(null);
  
  // Multi-select states
  const [selectedEvaluations, setSelectedEvaluations] = useState([]);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  
  // Permission states
  const [rolePermissions, setRolePermissions] = useState({});
  
  // Edit state
  const [editingEvaluation, setEditingEvaluation] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const isAdmin = evaluator?.role === 'admin';
  
  // Check if user can edit/delete evaluations
  const canEditEvaluations = isAdmin || (rolePermissions[evaluator?.role]?.edit_evaluations === true);
  const canDeleteEvaluations = isAdmin || (rolePermissions[evaluator?.role]?.can_delete_evaluations === true);
  const canBlockEvaluations = isAdmin || (rolePermissions[evaluator?.role]?.can_block_evaluations === true);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [evalRes, playersRes, evalsRes, permsRes] = await Promise.all([
        axios.get(`${API}/evaluations`),
        axios.get(`${API}/players`),
        axios.get(`${API}/evaluators`),
        axios.get(`${API}/role-permissions`),
      ]);
      setEvaluations(evalRes.data || []);
      setPlayers(playersRes.data || []);
      setEvaluators(evalsRes.data || []);
      setRolePermissions(permsRes.data || {});
    } catch (error) {
      toast.error("Erro ao carregar avaliações");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Toggle selection of a single evaluation
  const toggleEvaluationSelection = (evalId) => {
    setSelectedEvaluations(prev => {
      if (prev.includes(evalId)) {
        return prev.filter(id => id !== evalId);
      } else {
        return [...prev, evalId];
      }
    });
  };

  // Select/deselect all visible evaluations
  const toggleSelectAll = () => {
    if (selectedEvaluations.length === filteredEvaluations.length) {
      setSelectedEvaluations([]);
    } else {
      setSelectedEvaluations(filteredEvaluations.map(e => e.id));
    }
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (selectedEvaluations.length === 0) return;
    
    setIsBulkDeleting(true);
    try {
      const response = await axios.post(`${API}/evaluations/bulk-delete`, {
        evaluation_ids: selectedEvaluations
      });
      toast.success(response.data.message);
      setSelectedEvaluations([]);
      setBulkDeleteDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao excluir avaliações");
    } finally {
      setIsBulkDeleting(false);
    }
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedEvaluations([]);
  };

  const handleDeleteEvaluation = async () => {
    if (!evaluationToDelete) return;
    
    setIsDeleting(true);
    try {
      await axios.delete(`${API}/evaluations/${evaluationToDelete.id}`);
      toast.success("Avaliação excluída com sucesso");
      setDeleteDialogOpen(false);
      setEvaluationToDelete(null);
      fetchData(); // Reload data
    } catch (error) {
      toast.error("Erro ao excluir avaliação");
    } finally {
      setIsDeleting(false);
    }
  };

  const openDeleteDialog = (evaluation, e) => {
    e.stopPropagation();
    setEvaluationToDelete(evaluation);
    setDeleteDialogOpen(true);
  };

  const handleToggleBlock = async (evaluation, e) => {
    e.stopPropagation();
    setTogglingBlockId(evaluation.id);
    try {
      const res = await axios.patch(`${API}/evaluations/${evaluation.id}/toggle-block`);
      toast.success(res.data.message);
      // Update local state
      setEvaluations(prev => prev.map(ev => 
        ev.id === evaluation.id ? { ...ev, blocked: res.data.blocked } : ev
      ));
    } catch (error) {
      toast.error("Erro ao alterar status da avaliação");
    } finally {
      setTogglingBlockId(null);
    }
  };

  // Step 1: Filter evaluations
  const filteredOnly = useMemo(() => {
    const now = new Date();
    const periodDays = selectedPeriod === "7d" ? 7 : selectedPeriod === "30d" ? 30 : null;

    return evaluations.filter((ev) => {
      const evDate = ev.created_at ? new Date(ev.created_at) : null;

      if (selectedPlayer !== "all" && ev.player_id !== selectedPlayer) return false;
      if (selectedEvaluator !== "all" && ev.evaluator_id !== selectedEvaluator) return false;

      if (periodDays && evDate) {
        const diffDays = (now - evDate) / (1000 * 60 * 60 * 24);
        if (diffDays > periodDays) return false;
      }

      if (searchQuery.trim()) {
        const player = ev.player || players.find(p => p.id === ev.player_id);
        const playerName = player ? (player.first_name ? `${player.first_name} ${player.last_name}` : player.name || "") : "";
        if (!playerName.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      }

      return true;
    });
  }, [evaluations, players, selectedPlayer, selectedEvaluator, selectedPeriod, searchQuery]);

  // Step 2: Sort filtered evaluations
  const filteredEvaluations = useMemo(() => {
    return [...filteredOnly].sort((a, b) => {
      if (sortBy === "average") {
        const avgA = parseFloat(calcAverage(a.skills, a.setter_skills));
        const avgB = parseFloat(calcAverage(b.skills, b.setter_skills));
        return sortDirection === "asc" ? avgA - avgB : avgB - avgA;
      }
      const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
      const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
      return sortDirection === "asc" ? dateA - dateB : dateB - dateA;
    });
  }, [filteredOnly, sortBy, sortDirection]);

  const getPlayerName = (player) => {
    if (!player) return "-";
    if (player.first_name) return `${player.first_name} ${player.last_name}`;
    return player.name || "-";
  };

  const getEvaluatorName = (ev) => ev?.name || "-";

  const formatDate = (date) => {
    if (!date) return "-";
    try {
      return format(new Date(date), "dd/MM/yyyy HH:mm", { locale: ptBR });
    } catch {
      return "-";
    }
  };

  const calcAverage = (skills, setterSkills) => {
    // Try regular skills first
    if (skills && Object.keys(skills).length > 0) {
      const values = Object.values(skills);
      return (values.reduce((a, b) => a + b, 0) / values.length).toFixed(1);
    }
    // Fall back to setter_skills for levantadores
    if (setterSkills && Object.keys(setterSkills).length > 0) {
      const values = Object.values(setterSkills);
      return (values.reduce((a, b) => a + b, 0) / values.length).toFixed(1);
    }
    return 0;
  };

  if (evaluator?.role !== "admin" && evaluator?.role !== "tecnico") {
    return (
      <PageLayout evaluator={evaluator} onLogout={onLogout} title={t('evaluationsPanel.title')}>
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            Apenas administradores e técnicos podem acessar este painel.
          </CardContent>
        </Card>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      evaluator={evaluator}
      onLogout={onLogout}
      title={t('evaluationsPanel.title')}
      subtitle={t('evaluationsPanel.subtitle')}
    >
      <Card className="bg-card/80 border-border/60 shadow-xl">
        <CardHeader className="pb-4">
          <CardTitle className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <span className="text-lg font-bold">Filtros</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Campo de busca por nome */}
          <div className="space-y-1">
            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
              Buscar por Nome do Jogador
            </Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder={t('evaluationsPanel.searchPlayer')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-input/50 border-border"
                data-testid="search-player-input"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            {/* Filtro por jogador */}
            <div className="space-y-1">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Jogador
              </Label>
              <Select value={selectedPlayer} onValueChange={setSelectedPlayer}>
                <SelectTrigger className="bg-input/50 border-border">
                  <SelectValue placeholder={t('evaluationsPanel.allPlayers')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {players.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {getPlayerName(p)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Filtro por avaliador */}
            <div className="space-y-1">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Avaliador
              </Label>
              <Select value={selectedEvaluator} onValueChange={setSelectedEvaluator}>
                <SelectTrigger className="bg-input/50 border-border">
                  <SelectValue placeholder={t('evaluationsPanel.allEvaluators')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {evaluators.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Filtro por período */}
            <div className="space-y-1">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Período
              </Label>
              <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                <SelectTrigger className="bg-input/50 border-border">
                  <SelectValue placeholder={t('evaluationsPanel.last30')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7d">Últimos 7 dias</SelectItem>
                  <SelectItem value="30d">{t('evaluationsPanel.last30')}</SelectItem>
                  <SelectItem value="all">{t('evaluationsPanel.allPeriod')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="mt-6">
        <Card className="bg-card/80 border-border/60 shadow-xl">
          <CardHeader className="pb-3 flex flex-row items-center justify-between gap-4">
            <CardTitle className="text-lg font-bold">{t('evaluationsPanel.allEvaluations')}</CardTitle>
            <div className="flex items-center gap-4">
              <div className="text-xs text-muted-foreground hidden sm:block">
                {filteredEvaluations.length} {t('evaluationsPanel.results')}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {/* Bulk Selection Controls - Only for users with delete permission */}
                {canDeleteEvaluations && (
                  <>
                    <Select
                      value=""
                      onValueChange={(evaluatorId) => {
                        if (evaluatorId === "all") {
                          // Selecionar todas as avaliações visíveis
                          setSelectedEvaluations(filteredEvaluations.map(e => e.id));
                        } else {
                          // Selecionar apenas avaliações do avaliador específico
                          const evaluatorEvals = filteredEvaluations
                            .filter(e => e.evaluator_id === evaluatorId)
                            .map(e => e.id);
                          setSelectedEvaluations(prev => {
                            const newSet = new Set([...prev, ...evaluatorEvals]);
                            return Array.from(newSet);
                          });
                        }
                      }}
                    >
                      <SelectTrigger className="w-[200px] h-10 bg-input/50">
                        <SelectValue placeholder={t('evaluationsPanel.selectByEvaluator')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos os Visíveis</SelectItem>
                        {evaluators.map(ev => {
                          const count = filteredEvaluations.filter(e => e.evaluator_id === ev.id).length;
                          return (
                            <SelectItem key={ev.id} value={ev.id} disabled={count === 0}>
                              {ev.name} ({count})
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    <Button
                      variant="outline"
                      size="default"
                      className="h-10 px-4"
                      onClick={toggleSelectAll}
                    >
                      {selectedEvaluations.length === filteredEvaluations.length && filteredEvaluations.length > 0 ? (
                        <>
                          <Square className="h-4 w-4 mr-2" />
                          Desmarcar Todos
                        </>
                      ) : (
                        <>
                          <CheckSquare className="h-4 w-4 mr-2" />
                          Selecionar Todos
                        </>
                      )}
                    </Button>
                    {selectedEvaluations.length > 0 && (
                      <>
                        <Button
                          variant="ghost"
                          size="default"
                          className="h-10 px-3 text-muted-foreground hover:text-foreground"
                          onClick={clearSelection}
                        >
                          <X className="h-4 w-4 mr-1" />
                          Limpar
                        </Button>
                        <Button
                          variant="destructive"
                          size="default"
                          className="h-10 px-4"
                          onClick={() => setBulkDeleteDialogOpen(true)}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Excluir ({selectedEvaluations.length})
                        </Button>
                      </>
                    )}
                  </>
                )}
                <Button
                  variant="outline"
                  size="default"
                  className="h-10 px-4"
                  onClick={async () => {
                    try {
                      const params = new URLSearchParams();
                      if (selectedPlayer !== "all") params.append("player_id", selectedPlayer);
                      if (selectedEvaluator !== "all") params.append("evaluator_id", selectedEvaluator);
                      if (selectedPeriod !== "all") params.append("period", selectedPeriod);
                      const url = `${API}/export/excel?${params.toString()}`;
                      window.open(url, "_blank");
                    } catch (error) {
                      toast.error("Erro ao exportar para Excel");
                    }
                  }}
                >
                  {t('evaluationsPanel.exportEvaluations')}
                </Button>
                <Button
                  variant="outline"
                  size="default"
                  className="h-10 px-4"
                  onClick={() => {
                    try {
                      const url = `${API}/export/excel/players`;
                      window.open(url, "_blank");
                    } catch (error) {
                      toast.error("Erro ao exportar resumo de jogadores");
                    }
                  }}
                >
                  {t('evaluationsPanel.exportPlayers')}
                </Button>
                <Button
                  variant="outline"
                  size="default"
                  className="h-10 px-4"
                  onClick={() => {
                    try {
                      const params = new URLSearchParams();
                      if (selectedPlayer !== "all") params.append("player_id", selectedPlayer);
                      if (selectedEvaluator !== "all") params.append("evaluator_id", selectedEvaluator);
                      if (selectedPeriod !== "all") params.append("period", selectedPeriod);
                      const url = `${API}/export/pdf?${params.toString()}`;
                      window.open(url, "_blank");
                    } catch (error) {
                      toast.error("Erro ao exportar PDF");
                    }
                  }}
                >
                  {t('evaluationsPanel.exportPDF')}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            {isLoading ? (
              <div className="p-6 space-y-3">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={`skel-${i}`} className="h-10 w-full bg-muted/60" />
                ))}
              </div>
            ) : (
              <table className="min-w-full text-sm">
                <thead className="bg-muted/40 border-b border-border/60">
                  <tr>
                    {canDeleteEvaluations && (
                      <th className="px-4 py-2 text-center w-10">
                        <Checkbox
                          checked={selectedEvaluations.length === filteredEvaluations.length && filteredEvaluations.length > 0}
                          onCheckedChange={toggleSelectAll}
                          className="border-muted-foreground"
                        />
                      </th>
                    )}
                    <th
                      className="px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase cursor-pointer select-none"
                      onClick={() => {
                        setSortBy("date");
                        setSortDirection((prev) => (sortBy === "date" && prev === "desc" ? "asc" : "desc"));
                      }}
                    >
                      Data {sortBy === "date" && (sortDirection === "desc" ? "↓" : "↑")}
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase">Jogador</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase">Avaliador</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase">Perfil</th>
                    <th
                      className="px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase cursor-pointer select-none"
                      onClick={() => {
                        setSortBy("average");
                        setSortDirection((prev) => (sortBy === "average" && prev === "desc" ? "asc" : "desc"));
                      }}
                    >
                      Média {sortBy === "average" && (sortDirection === "desc" ? "↓" : "↑")}
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-muted-foreground uppercase">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEvaluations.length === 0 ? (
                    <tr>
                      <td colSpan={canDeleteEvaluations ? 7 : 6} className="px-4 py-6 text-center text-muted-foreground">
                        {t('evaluationsPanel.noEvaluations')}
                      </td>
                    </tr>
                  ) : (
                    filteredEvaluations.map((ev) => {
                      const player = ev.player || players.find((p) => p.id === ev.player_id);
                      const evalUser = ev.evaluator || evaluators.find((e) => e.id === ev.evaluator_id);
                      return (
                        <EvaluationRow
                          key={ev.id}
                          ev={ev}
                          player={player}
                          evalUser={evalUser}
                          isSelected={selectedEvaluations.includes(ev.id)}
                          isAdmin={isAdmin}
                          canDeleteEvaluations={canDeleteEvaluations}
                          canBlockEvaluations={canBlockEvaluations}
                          togglingBlockId={togglingBlockId}
                          onToggleSelection={toggleEvaluationSelection}
                          onViewDetails={setSelectedEvaluation}
                          onEdit={(e) => { setEditingEvaluation(e); setEditDialogOpen(true); }}
                          onToggleBlock={handleToggleBlock}
                          onDelete={openDeleteDialog}
                          formatDate={formatDate}
                          t={t}
                        />
                      );
                    })
                  )}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Modal de detalhes da avaliação */}
      <Dialog open={!!selectedEvaluation} onOpenChange={() => setSelectedEvaluation(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Detalhes da Avaliação</DialogTitle>
            <DialogDescription>
              Visualize as notas por fundamento desta avaliação.
            </DialogDescription>
          </DialogHeader>
          {selectedEvaluation && (
            <div className="space-y-4">
              <div className="flex items-center justify-between text-sm">
                <div>
                  <p className="font-semibold">{getPlayerName(selectedEvaluation.player || players.find(p => p.id === selectedEvaluation.player_id))}</p>
                  <p className="text-xs text-muted-foreground">
                    Avaliador: {getEvaluatorName(selectedEvaluation.evaluator || evaluators.find(e => e.id === selectedEvaluation.evaluator_id))}
                  </p>
                </div>
                <div className="text-right text-xs text-muted-foreground">
                  <p>{formatDate(selectedEvaluation.created_at)}</p>
                  <p className="capitalize">Perfil: {selectedEvaluation.profile_type || 'jogador'}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                {Object.entries(selectedEvaluation.skills || {}).map(([key, value]) => (
                  <div
                    key={key}
                    className="flex items-center justify-between rounded-lg bg-muted/40 px-3 py-2 border border-border/50"
                  >
                    <span className="capitalize text-xs text-muted-foreground">{key}</span>
                    <span className="font-semibold text-sm">{value.toFixed(1)}</span>
                  </div>
                ))}
              </div>

              <div className="flex justify-end">
                <Button variant="outline" size="sm" onClick={() => setSelectedEvaluation(null)}>
                  Fechar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog de confirmação para excluir avaliação */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-500">Excluir Avaliação</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir esta avaliação?
              {evaluationToDelete && (
                <div className="mt-2 p-3 bg-muted/50 rounded-lg text-sm">
                  <p><strong>Jogador:</strong> {getPlayerName(players.find(p => p.id === evaluationToDelete.player_id))}</p>
                  <p><strong>Avaliador:</strong> {getEvaluatorName(evaluators.find(e => e.id === evaluationToDelete.evaluator_id))}</p>
                  <p><strong>Data:</strong> {formatDate(evaluationToDelete.created_at)}</p>
                </div>
              )}
              <p className="mt-2 text-red-400">Esta ação não pode ser desfeita.</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteEvaluation}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {isDeleting ? "Excluindo..." : "Excluir"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog de confirmação para exclusão em lote */}
      <AlertDialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-500">Excluir Avaliações em Lote</AlertDialogTitle>
            <AlertDialogDescription>
              <p>Tem certeza que deseja excluir <strong>{selectedEvaluations.length}</strong> avaliação(ões)?</p>
              <div className="mt-3 p-3 bg-muted/50 rounded-lg text-sm max-h-40 overflow-y-auto">
                {selectedEvaluations.slice(0, 10).map(evalId => {
                  const ev = evaluations.find(e => e.id === evalId);
                  const player = players.find(p => p.id === ev?.player_id);
                  return (
                    <p key={evalId} className="text-xs text-muted-foreground">
                      • {getPlayerName(player)} - {formatDate(ev?.created_at)}
                    </p>
                  );
                })}
                {selectedEvaluations.length > 10 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    ... e mais {selectedEvaluations.length - 10} avaliação(ões)
                  </p>
                )}
              </div>
              <p className="mt-3 text-red-400 font-medium">Esta ação não pode ser desfeita!</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isBulkDeleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              disabled={isBulkDeleting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {isBulkDeleting ? "Excluindo..." : `Excluir ${selectedEvaluations.length} avaliação(ões)`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog de edição de avaliação */}
      <EditEvaluationDialog
        evaluation={editingEvaluation}
        evaluators={evaluators}
        open={editDialogOpen}
        onOpenChange={(open) => {
          setEditDialogOpen(open);
          if (!open) setEditingEvaluation(null);
        }}
        onSaved={fetchData}
      />
    </PageLayout>
  );
}
